"use client";

import {JSX} from "react";

import Link from "next/link";

import SmartImage from "@/shared/ui/smart-image";

import {useTranslation} from "@/shared/i18n";
import {CurrencyUtils} from "@/shared/lib";
import {assetPath} from "@/shared/utils/asset-path";

import type {RentalCardProps} from "../rental-card/rental-card";

import "./rental-card-v2.scss";

/** 5 -> "5.0", 4.981 -> "4.98" */
function formatRating(rating: number): string {
    const rounded = Math.round(rating * 100) / 100;
    return Number.isInteger(rounded) ? rounded.toFixed(1) : String(rounded);
}

/**
 * Rental card, new design (v2): rounded image, title with a rating badge, location,
 * persons / beds / bathrooms with icons and the nightly price. The whole card links to the property.
 */
export function RentalCardV2({
    title,
    location,
    image,
    rating,
    persons,
    beds,
    bathrooms,
    pricePerNight,
    itinerary,
    link,
    blank,
}: RentalCardProps): JSX.Element {
    const {t} = useTranslation("rental");

    const amenities = [
        {icon: "people", text: t("cardPersons", {count: persons})},
        {icon: "bed", text: t("cardBeds", {count: beds})},
        {icon: "bath", text: t("cardBathrooms", {count: bathrooms})},
    ];

    return (
        <Link href={link ?? ""} className="rental-card-v2" target={blank ? "_blank" : undefined} rel={blank ? "noopener noreferrer" : undefined}>
            <div className="rental-card-v2__image">
                <SmartImage
                    src={image}
                    alt={title}
                    fill
                    className="rental-card-v2__img"
                    sizes="(max-width: 767px) 50vw, (max-width: 1200px) 33vw, 320px"
                />
            </div>

            <div className="rental-card-v2__head">
                <h3 className="rental-card-v2__title">{title}</h3>
                {rating > 0 && (
                    <span className="rental-card-v2__rating">
                        <span className="rental-card-v2__star" aria-hidden="true">
                            ★
                        </span>
                        {formatRating(rating)}
                    </span>
                )}
            </div>
            <p className="rental-card-v2__location">{location}</p>

            <ul className="rental-card-v2__amenities">
                {amenities.map((amenity) => (
                    <li key={amenity.icon} className="rental-card-v2__amenity">
                        <SmartImage src={assetPath(`/images/icons/property/${amenity.icon}.svg`)} alt="" width={14} height={14} />
                        {amenity.text}
                    </li>
                ))}
            </ul>

            <p className="rental-card-v2__price">
                <strong>{CurrencyUtils.formatUSD(pricePerNight, true)}</strong>
                <span className="rental-card-v2__price-note">
                    {t("perNight")}
                    {itinerary && ` · ${t("totalAmount", {amount: CurrencyUtils.formatUSD(itinerary.totalPrice, true)})}`}
                </span>
            </p>
        </Link>
    );
}
